import { useId, useState } from 'react';
import type { SkillTreeCompetency, SkillTreeTier } from './skill-tree-types';

// A single competency's skill tree: a horizontal branch of 5 RPG-tier
// nodes (Novice → Apprentice → Adept → Expert → Master). Unlocked
// nodes fill with the accent colour; locked nodes are dim. The
// connecting line fills up to the current progress. `prefers-reduced-
// motion` disables the glow pulse — the spec requires it.
//
// IMPORTANT: these tiers are gamification, NOT career levels. A
// player reaching "Master" has earned game XP, not a promotion. The
// Forbury framework's career-level names (junior/intermediate/senior/
// lead) are admin-only reference and never appear here. See ADR-0008.
//
// Accessibility: the SVG carries `role="img"` + `aria-label`. A
// visually-hidden ordered list gives screen readers the full tier
// details. A visible "About this competency" toggle shows the
// top-level description (what the competency covers) — NOT the
// per-level Forbury career descriptors.

const NODE_RADIUS = 16;
const NODE_GAP = 56;       // centre-to-centre horizontal spacing (was 64 with 5 nodes; tighter with 6 to fit the same width)
const NODE_Y = 44;         // vertical centre of the branch
const LINE_Y = NODE_Y;
const SVG_WIDTH = 5 * NODE_GAP + 2 * NODE_RADIUS * 2; // 6 nodes (Initiate..Master), pad either end
const SVG_HEIGHT = 88;

// Map tier number → x coordinate of the node centre.
// The leftmost node (tier 0, Initiate since 2026-07-19) sits
// at x = leftPad. Each subsequent tier is NODE_GAP to the
// right. The formula is `leftPad + tier * NODE_GAP` (not
// `leftPad + (tier - 1) * NODE_GAP`) because tier 0 now
// exists and needs the same leftPad as the rest.
//
// (Before tier 0 was added, the formula was
// `leftPad + (tier - 1) * NODE_GAP` with tiers 1..5, and
// the first node at x = leftPad because (1 - 1) = 0.
// That formula is now wrong: with tier 0 it would put
// the Initiate node at x = leftPad - NODE_GAP, which is
// negative and gets clipped off the left edge of the SVG.)
function nodeX(tier: number): number {
  const leftPad = NODE_RADIUS * 2;
  return leftPad + tier * NODE_GAP;
}

// Map a tier to its 0..1 fill percentage. Unlocked
// tiers are 1 (full). The next tier being worked toward
// gets a wedge proportional to XP progress within the
// (prev threshold, current threshold) span. Tiers further
// out stay at 0.
//
// Tier 0 (Initiate) is always unlocked (its xp_threshold
// is 0, and the api sets unlocked=true whenever
// gameLevel >= levelThreshold, which is true for any
// level >= 0). So for a brand-new player with 0 XP,
// highestUnlocked is 0 (Initiate), nextTierNumber is 1
// (Novice), and the Novice circle gets the partial wedge.
// This is the "you've started" state.
//
// Why this lives in the view (not the api):
//   - The api already exposes xp_threshold per tier and
//     the player's total xp, which is enough to compute
//     the fill client-side. Adding a fill_pct field to
//     every tier in the response would be a small
//     denormalization for one UI affordance.
//   - The math is the same as the unlock check, so the
//     visual fill and the unlock flip are guaranteed to
//     be in sync (the api's threshold is the source of
//     truth, the view derives the wedge from it).
//   - Keeping it client-side means the api response
//     shape stays unchanged and other consumers (e.g.
//     future tooltips, the dashboard skill list) can
//     derive the same value without a separate contract.
function tierFillPct(
  tier: SkillTreeTier,
  allTiers: SkillTreeTier[],
  currentXp: number,
): number {
  if (tier.unlocked) return 1;
  // The next tier is the one whose tier number is the
  // smallest above the highest unlocked. The Initiate
  // tier is always unlocked, so for a brand-new player
  // highestUnlocked = 0 and nextTierNumber = 1 (Novice).
  const highestUnlocked = allTiers
    .filter((t) => t.unlocked)
    .reduce((max, t) => Math.max(max, t.tier), 0);
  const nextTierNumber = highestUnlocked + 1;
  if (tier.tier !== nextTierNumber) return 0;
  // Find the previous tier's threshold. For tier 1
  // (Novice), the previous tier is tier 0 (Initiate)
  // whose xp_threshold is 0. For tier 2 (Apprentice),
  // the previous is tier 1 (Novice, 100 XP). Etc.
  const prevThreshold = highestUnlocked === 0
    ? 0
    : allTiers.find((t) => t.tier === highestUnlocked)?.xp_threshold ?? 0;
  const span = tier.xp_threshold - prevThreshold;
  if (span <= 0) return 1;  // Defensive: same threshold means tier is already met.
  const into = currentXp - prevThreshold;
  return Math.max(0, Math.min(1, into / span));
}

export function SkillTree({ competency }: { competency: SkillTreeCompetency }) {
  const [showAbout, setShowAbout] = useState(false);
  const reduceMotion = usePrefersReducedMotion();
  // M5.11: unique prefix for the per-competency SVG
  // gradient IDs. Multiple competencies can render on
  // the same page (My Skills shows all 12), and SVG
  // <linearGradient> IDs are document-scoped — a
  // shared ID across SVGs would mean the FIRST
  // competency's gradient applies to all of them.
  // useId() gives a per-render unique prefix; the
  // competency id is also in the mix for stability
  // across re-renders.
  const reactId = useId();
  const gradId = (kind: 'base' | 'fill') => `gt-${kind}-${competency.id}-${reactId.replace(/:/g, '')}`;

  // The highest unlocked tier (0 if none of the named
  // tiers are unlocked; the Initiate tier is always
  // unlocked so this is essentially "the highest
  // non-Initiate tier reached", which is 0 for a
  // brand-new player with 0 XP and N for a player
  // who's earned enough XP to light up tier N).
  const highestUnlocked = competency.tiers
    .filter((t) => t.unlocked)
    .reduce((max, t) => Math.max(max, t.tier), 0);

  // The fill line goes from the first node (Initiate,
  // tier 0) to the current progress position. Partial
  // fill toward the next tier based on XP progress
  // within the current game-level span. With tier 0
  // added on 2026-07-19, the leftmost endpoint is now
  // nodeX(0) instead of nodeX(1); brand-new players
  // see the line begin at Initiate and grow rightward
  // toward Novice.
  const fillEndX = (() => {
    if (highestUnlocked >= 5) return nodeX(5);
    const next = competency.tiers.find((t) => t.tier === highestUnlocked + 1);
    const current = competency.tiers.find((t) => t.tier === highestUnlocked);
    if (!next || !current) return nodeX(0);
    const span = next.xp_threshold - current.xp_threshold;
    const into = competency.xp - current.xp_threshold;
    const frac = span > 0 ? Math.min(1, Math.max(0, into / span)) : 0;
    return nodeX(highestUnlocked) + frac * (nodeX(highestUnlocked + 1) - nodeX(highestUnlocked));
  })();

  const ariaLabel = `${competency.name}: game level ${competency.level}, ${competency.xp} XP. ` +
    competency.tiers
      .map((t) => {
        const pct = Math.round(tierFillPct(t, competency.tiers, competency.xp) * 100);
        // For unlocked tiers, say "unlocked". For the
        // in-progress tier, say "in progress (65%)".
        // For tiers further out, say "locked".
        const status = t.unlocked
          ? 'unlocked'
          : pct > 0
            ? `in progress (${pct}%)`
            : 'locked';
        return `${t.name} ${status} at ${t.xp_threshold} XP`;
      })
      .join('; ');

  return (
    <div className="card">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-base font-semibold">{competency.name}</h3>
          <p className="text-xs uppercase tracking-wide text-ink-dim">
            {competency.dimension}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold tabular-nums">{competency.xp} XP</p>
          <p className="text-xs text-ink-muted">Level {competency.level}</p>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        className="mt-3 w-full"
        role="img"
        aria-label={ariaLabel}
      >
        {/* Base line (dim) from first to last node.
            The first node is tier 0 (Initiate) since
            2026-07-19; the last is tier 5 (Master).
            M5.11: the line is now a linear gradient
            through the tier colours so the colour
            journey is visible even before any tier
            is unlocked. A new player at tier 0 sees
            a full white→grey→blue→purple→amber→gold
            line, which communicates "the colours you
            will earn" without saying it in words.
            The gradient is defined as a <defs>
            element per-SVG; one per competency. */}
        <defs>
          <linearGradient id={gradId(`base`)} x1={nodeX(0)} y1={0} x2={nodeX(5)} y2={0} gradientUnits="userSpaceOnUse">
            {[0, 1, 2, 3, 4, 5].map((t) => (
              <stop
                key={t}
                offset={`${(t / 5) * 100}%`}
                stopColor={tierColor(t)}
                stopOpacity={0.35}
              />
            ))}
          </linearGradient>
          {/* Progress fill: gradient from the current
              tier colour to the next tier colour, so
              the fill line itself carries the colour
              journey. For a player at tier 2 (Apprentice)
              filling toward tier 3 (Adept), the fill is
              a blue→purple gradient. When the player is
              exactly at a threshold (e.g. tier 3 just
              unlocked), the gradient collapses to a
              solid colour (the new tier's own colour). */}
          <linearGradient id={gradId(`fill`)} x1={nodeX(0)} y1={0} x2={nodeX(5)} y2={0} gradientUnits="userSpaceOnUse">
            {[0, 1, 2, 3, 4, 5].map((t) => (
              <stop
                key={t}
                offset={`${(t / 5) * 100}%`}
                stopColor={tierColor(t)}
                stopOpacity={1}
              />
            ))}
          </linearGradient>
        </defs>
        <line
          x1={nodeX(0)} y1={LINE_Y} x2={nodeX(5)} y2={LINE_Y}
          stroke={`url(#${gradId(`base`)})`}
          strokeWidth={3} strokeLinecap="round"
        />
        {/* Progress fill line. Starts at Initiate
            (nodeX(0)) so a brand-new player with 0 XP
            shows no fill (the line is just the dim
            base) and a player at 50 XP shows a fill
            from Initiate to the partial-progress
            position toward Novice. M5.11: uses the
            gradient above instead of the flat accent
            purple. */}
        {fillEndX > nodeX(0) && (
          <line
            x1={nodeX(0)} y1={LINE_Y} x2={fillEndX} y2={LINE_Y}
            stroke={`url(#${gradId(`fill`)})`}
            strokeWidth={3}
            strokeLinecap="round"
          />
        )}
        {/* Nodes */}
        {competency.tiers.map((t) => (
          <TierNode
            key={t.tier}
            tier={t}
            x={nodeX(t.tier)}
            y={NODE_Y}
            // M5.8+: per-tier fill percentage. Unlocked
            // tiers are full (1). The next tier being
            // worked toward gets a wedge proportional to
            // XP progress within its span. Tiers further
            // out stay at 0.
            //
            // `tierFillPct` is a small pure helper (kept
            // inline because it's used in exactly one
            // place) that returns 0..1 for the partial
            // wedge. It mirrors the same math the api
            // uses to decide when a tier unlocks, so the
            // visual fill is always exactly in sync with
            // the unlock logic.
            fillPct={tierFillPct(t, competency.tiers, competency.xp)}
            // M5.11: pass whether this tier is the
            // "current" one (highestUnlocked, or the
            // in-progress tier when mid-fill). The
            // current tier gets a soft static glow.
            isCurrent={
              t.tier === highestUnlocked ||
              // In-progress tier: highestUnlocked + 1
              // when the player has some XP into the
              // next tier's span.
              (t.tier === highestUnlocked + 1 &&
                tierFillPct(t, competency.tiers, competency.xp) > 0)
            }
            reduceMotion={reduceMotion}
          />
        ))}
      </svg>

      {/* Screen-reader-only list: full tier details */}
      <ol className="sr-only">
        {competency.tiers.map((t) => {
          const pct = Math.round(tierFillPct(t, competency.tiers, competency.xp) * 100);
          // M5.11: distinguish "current" (highest
          // unlocked, or in-progress toward the next)
          // from "unlocked" (already passed) so screen
          // readers can hear "you are here" the same
          // way sighted users see the soft glow on the
          // current tier node.
          const isCurrent =
            t.tier === highestUnlocked ||
            (t.tier === highestUnlocked + 1 && pct > 0);
          const status = isCurrent
            ? t.unlocked
              ? `current (unlocked at ${t.xp_threshold} XP)`
              : `current (in progress, ${pct}% to next)`
            : t.unlocked
              ? 'unlocked'
              : pct > 0
                ? `in progress (${pct}%)`
                : 'locked';
          return (
            <li key={t.tier}>
              {t.name} (tier {t.tier}): {status}. Requires {t.xp_threshold} XP (game level {t.level_threshold}).
            </li>
          );
        })}
      </ol>

      <button
        className="mt-3 text-xs text-ink-muted underline hover:text-ink"
        onClick={() => setShowAbout((v) => !v)}
        aria-expanded={showAbout}
      >
        {showAbout ? 'Hide details' : 'About this competency'}
      </button>
      {showAbout ? (
        <div className="mt-3 space-y-2 text-sm text-ink-muted">
          <p>{competency.description}</p>
          <TierTable tiers={competency.tiers} currentXp={competency.xp} />
        </div>
      ) : null}
    </div>
  );
}

// M5.11: tier color scale. Each tier has its own color
// in the progression from base (tier 0, white) to gold
// (tier 5, master). The scale pairs a cool grey start
// with a hot gold finish so the eye reads the
// progression as a single journey from "raw" to
// "forged". The exact hexes are pinned here (rather
// than read from CSS variables) because the SVG
// <line> and <circle> elements need literal color
// values for their stroke/fill props; Tailwind's
// CSS-variable-based colours don't work in inline
// SVG attributes without extra plumbing. The
// `accent` purple (tier 3) is the brand colour and
// shows up exactly mid-progression.
const TIER_COLORS: Record<number, string> = {
  0: '#e6e8ee', // Initiate   — white, base state
  1: '#9aa0ac', // Novice     — cool grey, unalloyed iron
  2: '#60a5fa', // Apprentice — sky blue, first real colour
  3: '#7c5cff', // Adept      — accent purple, earned brand colour
  4: '#f59e0b', // Expert     — amber, pre-gold glow
  5: '#fbbf24', // Master     — bright gold, the summit
};
function tierColor(tier: number): string {
  return TIER_COLORS[tier] ?? TIER_COLORS[0]!;
}
// Locked-future tier colour: same scale, low opacity.
// The "carrot" — the player can see what colour the
// next tier will be before they earn it. 0.25 opacity
// keeps it as a visual hint, not a competing signal.
const LOCKED_FUTURE_OPACITY = 0.25;

function TierNode({
  tier,
  x,
  y,
  fillPct,
  isCurrent,
  reduceMotion,
}: {
  tier: SkillTreeTier;
  x: number;
  y: number;
  // M5.8+: partial fill of the circle's interior. 0 = no
  // fill (locked tiers beyond the next), 1 = full fill
  // (the unlocked tiers and the next tier when the player
  // is exactly at the threshold), 0..1 = wedge (the next
  // tier being worked toward). Used to show "you're 65%
  // of the way to Adept" at a glance.
  fillPct: number;
  // M5.11: true for the player's CURRENT tier — the
  // highest unlocked tier, or the in-progress tier
  // when the player is mid-fill toward the next one.
  // The current tier gets a soft static glow (no
  // animation) so the eye can spot "you are here"
  // without a continuous pulse. Only one node is
  // current at a time; the rest are past-unlocked
  // (no glow) or future-locked (no glow).
  isCurrent: boolean;
  reduceMotion: boolean;
}) {
  const unlocked = tier.unlocked;
  const color = tierColor(tier.tier);
  // M5.11: future-locked tiers (above the player's
  // current tier) render at the same scale colour
  // with low opacity. This is the "carrot" — the
  // player can see what colour tier 3 (purple) or
  // tier 5 (gold) will be before they earn it. It
  // makes the progression read as a continuous
  // colour journey from base to summit. Without
  // this, locked tiers were a flat dim grey which
  // hid the colour gradient.
  const isFuture = !unlocked && !isCurrent;
  const fill = unlocked ? color : 'transparent';
  const stroke = unlocked || isFuture
    ? color
    : 'var(--bg-elevated, #333)';
  const strokeOpacity = isFuture ? LOCKED_FUTURE_OPACITY : 1;
  const textColour = unlocked ? 'var(--bg, #000)' : 'var(--ink-dim, #888)';
  // Clamp the partial fill to [0, 1]. The next tier is
  // shaded; everything else is 0 (locked) or 1 (unlocked
  // / exactly at the threshold).
  const pct = Math.max(0, Math.min(1, fillPct));
  // We render the partial fill as a wedge using a clipPath:
  // a rect of width (pct * diameter) starting at the left
  // edge of the bounding box clips a solid-fill circle so
  // only the leftmost pct is visible. This gives a clean
  // horizontal "filling up" effect that matches the
  // progress line (left-to-right). The full fill
  // (unlocked) bypasses the clip and uses a solid circle
  // for visual clarity.
  const clipId = unlocked ? undefined : `tier-fill-${tier.tier}-${x}`;
  // Soft static glow on the CURRENT tier. The
  // SVG <filter> with feGaussianBlur blurs a copy of
  // the node circle and renders it underneath, giving
  // a halo of the tier's own colour. Opacity 0.4 is
  // visible-but-soft; a full pulse was removed in
  // M5.11 (too distracting), but a static glow is
  // the right "you are here" signal. No animation
  // — just a steady tint.
  const glowId = `tier-glow-${tier.tier}-${x}`;
  return (
    <g>
      {/* Soft static glow on the current tier. The
          filter blurs the node circle and renders it
          underneath at 0.4 opacity in the tier's
          colour. Only the current tier renders this;
          past-unlocked and future-locked tiers are
          shown without a glow so the "you are here"
          signal is unambiguous. SVG filters are well-
          supported in all modern browsers; the
          performance cost is negligible for a single
          tier on a single SVG. The filter region
          is bounded so the blur doesn't bleed
          outside the SVG box. */}
      {isCurrent && !reduceMotion ? (
        <>
          <defs>
            <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
            </filter>
          </defs>
          <circle
            cx={x} cy={y} r={NODE_RADIUS}
            fill={color}
            opacity={0.4}
            filter={`url(#${glowId})`}
          />
        </>
      ) : null}
      {/* M5.11: glow ring around unlocked tiers was
          removed per user request — it competed with
          the static node circle for attention and
          didn't add information. The base circle
          below shows the unlocked state clearly on
          its own (filled vs outlined). The CSS
          @keyframes mirabili-pulse + the
          .mirabili-pulse class are still in
          index.css in case we want to reuse the
          animation elsewhere (e.g. a new-tier unlock
          celebration), but no element renders the
          ring any more. */}
      {/* Base circle (always rendered so the outline is
          visible on locked + partially-filled tiers).
          Past-unlocked tiers use a slightly darker
          variant of the tier colour as the stroke so
          the unlocked "trail" reads as a sequence
          (white → grey → blue → purple → amber → gold)
          rather than a flat accent wall. */}
      <circle
        cx={x} cy={y} r={NODE_RADIUS}
        fill={fill} stroke={stroke} strokeWidth={2.5}
        strokeOpacity={strokeOpacity}
      />
      {/* Partial fill: a clipped solid-fill circle that
          shows only the leftmost pct of the interior.
          Rendered BEFORE the tier number text so the digit
          sits on top. Only for the in-progress tier. Uses
          the tier's own colour (so a player working
          toward Novice sees a grey fill, toward Adept
          sees a purple fill, etc.) rather than the
          generic accent purple. */}
      {!unlocked && pct > 0 && clipId ? (
        <>
          <defs>
            <clipPath id={clipId}>
              <rect
                x={x - NODE_RADIUS}
                y={y - NODE_RADIUS}
                width={pct * NODE_RADIUS * 2}
                height={NODE_RADIUS * 2}
              />
            </clipPath>
          </defs>
          <circle
            cx={x} cy={y} r={NODE_RADIUS - 1.25}
            fill={color}
            opacity={0.7}
            clipPath={`url(#${clipId})`}
          />
        </>
      ) : null}
      <text
        x={x} y={y + 4} textAnchor="middle"
        fontSize={12} fontWeight={700} fill={textColour}
        style={{ pointerEvents: 'none' }}
      >
        {tier.tier}
      </text>
      <text
        x={x} y={NODE_Y + NODE_RADIUS + 18} textAnchor="middle"
        fontSize={10} fill="var(--ink-muted, #999)"
        style={{ pointerEvents: 'none' }}
      >
        {tier.name}
      </text>
    </g>
  );
}

function TierTable({
  tiers,
  currentXp,
}: {
  tiers: SkillTreeTier[];
  currentXp: number;
}) {
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="text-xs uppercase tracking-wide text-ink-dim">
          <th className="py-1 pr-2 font-medium">Tier</th>
          <th className="py-1 pr-2 font-medium">XP needed</th>
          <th className="py-1 font-medium">Status</th>
        </tr>
      </thead>
      <tbody>
        {tiers.map((t) => {
          const pct = Math.round(tierFillPct(t, tiers, currentXp) * 100);
          // The "in progress" status applies to the
          // single tier the player is currently working
          // toward. Unlocked tiers are "Unlocked";
          // tiers further out are "Locked".
          const status: 'unlocked' | 'in_progress' | 'locked' = t.unlocked
            ? 'unlocked'
            : pct > 0
              ? 'in_progress'
              : 'locked';
          return (
            <tr key={t.tier} className={t.unlocked ? '' : 'opacity-60'}>
              <td className="py-1 pr-2 align-top">
                <span className="font-medium">{t.name}</span>
              </td>
              <td className="py-1 pr-2 align-top tabular-nums">{t.xp_threshold} XP</td>
              <td className="py-1 align-top">
                {status === 'unlocked' ? (
                  <span className="text-accent-success">Unlocked</span>
                ) : status === 'in_progress' ? (
                  <span className="text-accent">In progress · {pct}%</span>
                ) : (
                  <span className="text-ink-dim">Locked</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className="pt-2 text-xs text-ink-dim">
            Current: {currentXp} XP (game level {Math.floor(Math.pow(currentXp / 100, 2 / 3))})
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

// Small hook for prefers-reduced-motion. SSR-safe.
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches !== reduce) setReduce(mq.matches);
  }
  return reduce;
}